/**
 * Team Accounts Card Component
 * Special stat card for displaying team size and account usage
 * Used in Company Dashboard
 */

import React from 'react';
import { COLORS, SPACING, TYPOGRAPHY, BORDER_RADIUS, SHADOWS } from '../../constants/theme';

export interface TeamAccountsCardProps {
  teamSize: number;
  totalUsers: number;
  maxUsers: number;
}

export const TeamAccountsCard: React.FC<TeamAccountsCardProps> = ({
  teamSize,
  totalUsers,
  maxUsers
}) => {
  const slotsRemaining = maxUsers - totalUsers;
  const usagePercent = Math.round((totalUsers / maxUsers) * 100);

  const getUsageColor = () => {
    if (usagePercent >= 90) return COLORS.danger.main;
    if (usagePercent >= 70) return COLORS.warning.main;
    return COLORS.success.main;
  };

  return (
    <div style={{
      backgroundColor: COLORS.background.primary,
      padding: SPACING.xl,
      borderRadius: BORDER_RADIUS.xl,
      border: `1px solid ${COLORS.border.light}`,
      boxShadow: SHADOWS.sm,
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: SPACING.md,
        marginBottom: SPACING.lg,
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: COLORS.primary.light,
          borderRadius: BORDER_RADIUS.lg,
          color: COLORS.primary.dark,
          flexShrink: 0,
        }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
        </div>
        <span style={{
          fontSize: TYPOGRAPHY.fontSize.sm,
          fontWeight: TYPOGRAPHY.fontWeight.semibold,
          color: COLORS.text.secondary,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}>
          Team & Accounts
        </span>
      </div>

      {/* Workers Count */}
      <div style={{ marginBottom: SPACING.md }}>
        <div style={{
          fontSize: TYPOGRAPHY.fontSize.sm,
          color: COLORS.text.secondary,
          marginBottom: SPACING.xs,
        }}>
          Workers
        </div>
        <div style={{
          fontSize: TYPOGRAPHY.fontSize['3xl'],
          fontWeight: TYPOGRAPHY.fontWeight.bold,
          color: COLORS.primary.main,
        }}>
          {teamSize}
        </div>
      </div>

      {/* Account Usage */}
      <div style={{ marginBottom: SPACING.sm }}>
        <div style={{
          fontSize: TYPOGRAPHY.fontSize.sm,
          color: COLORS.text.secondary,
          marginBottom: SPACING.xs,
        }}>
          Account Usage
        </div>
        <div style={{
          fontSize: TYPOGRAPHY.fontSize.xl,
          fontWeight: TYPOGRAPHY.fontWeight.semibold,
          color: COLORS.info.main,
        }}>
          {totalUsers} / {maxUsers}
          <span style={{
            fontSize: TYPOGRAPHY.fontSize.sm,
            color: COLORS.text.tertiary,
            fontWeight: TYPOGRAPHY.fontWeight.normal,
            marginLeft: SPACING.sm,
          }}>
            ({slotsRemaining} {slotsRemaining === 1 ? 'slot' : 'slots'} left)
          </span>
        </div>
      </div>

      {/* Usage Progress Bar */}
      <div style={{
        width: '100%',
        height: '6px',
        backgroundColor: COLORS.gray[200],
        borderRadius: BORDER_RADIUS.full,
        overflow: 'hidden',
        marginTop: SPACING.sm,
      }}>
        <div style={{
          width: `${usagePercent}%`,
          height: '100%',
          backgroundColor: getUsageColor(),
          transition: 'width 0.3s ease',
        }} />
      </div>
    </div>
  );
};
